import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TableContainer,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import { LoadingButton } from "@mui/lab";
import { AuthContext } from "../../../context/AuthContext";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const AddTimeEntriesDialog = ({ isoDate, open, handleClose, onSuccess }) => {
  const theme = useTheme();
  const { ezorder, authToken } = useContext(AuthContext);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  //   Get Users elegible for time entries for this day
  //   Double check with the current time card list if there's already an unsaved timecard on the UI
  const getAvailableUsersForTimeEntry = async () => {
    try {
      setLoadingUsers(true);
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };

      const response = await ezorder.get(
        `/time-entries/${isoDate}/available-users`,
        data
      );
      let availableUsers = response.data.users;
      for (let user of availableUsers) {
        // Select User
        user.checked = true;

        // Parse passed "YYYY-MM-DD" isoDate 24-hour "HH:mm Z" workStartTime into a new dayjs object
        // to reformat into 12 hour "hh:mm a" and manipulate for stop time
        // by adding the workDurationMinutes
        user.start = dayjs(
          `${isoDate} ${user.workStartTime}`,
          "YYYY-MM-DD HH:mm Z"
        ).tz(DEFAULT_TIMEZONE);
        user.startTime = user.start.format("hh:mm a");
        user.stop = user.start.add(user.workDurationMinutes, "minutes");
        user.stopTime = user.stop.format("hh:mm a");
        user.timeEntryDate = user.start.tz(DEFAULT_TIMEZONE).startOf("d");
        user.workMinutes =
          user.stop.diff(user.start, "minutes") -
          Number(user.lunchDurationMinutes ?? 0);
      }

      // console.log(response.data);
      setUsers(availableUsers);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      let newTimeEntries = [];
      for (let user of users) {
        if (user.checked) {
          newTimeEntries.push({
            // Start Of Day Phoenix Arizona 00:00 -07:00 But stored as UTC (7 hours behind so it's one day behind)
            date: user.timeEntryDate,
            start: user.start,
            stop: user.stop,
            workMinutes: user.workMinutes,
            noWork: false,
            noWorkReason: null,
            lunchDurationMinutes: user.lunchDurationMinutes,
            breakMinutes: 0,
            breakReason: null,
            user: user,
          });
        }
      }

      let response = await ezorder.post(
        `/time-entries`,
        { timeEntries: newTimeEntries },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        // navigate(`/time-entries`);
        onSuccess();
        handleClose();
        getAvailableUsersForTimeEntry(); // refresh available user list
      } else {
        setErrorMessage(response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        setErrorMessage(error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        setErrorMessage(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      getAvailableUsersForTimeEntry();
    }
  }, [isoDate, open]);

  // useEffect(() => {
  //   console.log(users);
  // }, [users]);

  return (
    <Dialog
      keepMounted
      scroll={"paper"}
      maxWidth="sm"
      fullWidth={true}
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>Add Time Entries</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer sx={{ maxHeight: 250 }} component={Box}>
          <Table sx={{}}>
            <TableHead>
              <TableRow>
                <TableCell align="left"></TableCell>
                <TableCell align="left">Name</TableCell>
                <TableCell align="left">Start</TableCell>
                <TableCell align="left">Stop</TableCell>
                <TableCell align="left">Lunch Mins</TableCell>
                {/* <TableCell align="left">Other Break</TableCell> */}
                {/* <TableCell align="left">Hours</TableCell> */}
                {/* <TableCell align="left">Week Total</TableCell> */}
                {/* <TableCell align="left">No Work</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingUsers ? (
                <TableRow hover>
                  <TableCell align="center" colspan={20}>
                    <CircularProgress
                      color="primary"
                      //   variant="determinate"
                      size="1.5rem"
                      //   value={uploadProgress}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {users &&
                    users.map((user, index) => {
                      return (
                        <TableRow
                          hover
                          key={index}
                          sx={{}}
                          // onClick={() => {
                          //   setSelectedInvoice(inv);
                          // }}
                        >
                          <TableCell align="left">
                            <Checkbox
                              // disabled
                              style={{
                                magin: 0,
                                padding: 0,
                              }}
                              checked={user.checked}
                              onChange={(e) => {
                                let usersCopy = [...users];
                                usersCopy[index].checked = e.target.checked;
                                setUsers(usersCopy);
                              }}
                            />
                          </TableCell>
                          <TableCell align="left">{user.fullName}</TableCell>
                          <TableCell align="left">{user.startTime}</TableCell>
                          <TableCell align="left"> {user.stopTime}</TableCell>
                          <TableCell align="left">
                            {user.lunchDurationMinutes}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ marginTop: "16px" }}>
          {errorMessage && (
            <Box>
              <Typography color={"red"}>{errorMessage}</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          onClick={() => {
            setErrorMessage("");
            handleClose();
          }}
          loading={loading || loadingUsers}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          variant="contained"
          loading={loading || loadingUsers}
          onClick={handleSubmit}
        >
          Add Time Cards
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
export default AddTimeEntriesDialog;
